import DOMPurify from 'dompurify'
import linkifyHtml from 'linkify-html'

/**
 * Render profiles — the single vocabulary for turning a stored value into safe
 * display HTML. Pick by the CONTENT CLASS of the field, not by convenience:
 *
 *   PLAIN — escape only. Literal `<gene>` is preserved as `&lt;gene&gt;`. Use for
 *           bare text injected into manual DOM / tooltips (`innerHTML`, template strings).
 *   TEXT  — escape, then add OUR OWN trusted markup (newline→<br>, linkified URLs).
 *           Use for plain-text field display via `v-html` (StringView, notes).
 *   RICH  — sanitize genuine HTML with a DOMPurify allowlist. Use ONLY where the
 *           value is authored as HTML (markdown/admin content). Minimal here on
 *           purpose; the full rich allowlist arrives with the tiptap migration.
 *
 * The rule: store raw, escape/sanitize at RENDER. Input handlers must never strip
 * or sanitize — literal `<` / `>` a user types must reach the store untouched.
 */
export const Profile = { PLAIN: 'PLAIN', TEXT: 'TEXT', RICH: 'RICH' }

const HTML_ESCAPES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

/**
 * Escape the five HTML-significant characters so a plain-text value renders
 * verbatim inside an HTML context. Lossless and reversible — the opposite of
 * stripping. This is what preserves scientists' `<gene>` / `T<cell` / `a < b`.
 */
export function escapeHtml(value = '') {
  return String(value ?? '').replace(/[&<>"']/g, (ch) => HTML_ESCAPES[ch])
}

/**
 * Turn a stored value into safe display HTML per the chosen render Profile.
 *
 * TEXT is the load-bearing case: it escapes FIRST, then linkifies (with nl2br).
 * Escaping before any markup is inserted means the user's `<` is already `&lt;`
 * by the time we add our trusted `<a>` / `<br>`, so `<gene>` survives and there
 * is no XSS surface. Reversing the order re-introduces the data-loss bug.
 */
export function render(value, profile = Profile.TEXT) {
  const raw = value ?? ''
  switch (profile) {
    case Profile.PLAIN:
      return escapeHtml(raw)
    case Profile.RICH:
      return sanitizeHtml(raw)
    case Profile.TEXT:
    default:
      return linkifyHtml(escapeHtml(raw), {
        nl2br: true,
        validate: { url: (url) => /^https?:\/\//.test(url) },
        target: '_blank',
        rel: 'noopener noreferrer',
      })
  }
}

export function sanitizeHtml(html, allowedTags = [], allowedAttributes = {}) {
  // Preserve existing link navigation while DOMPurify filters executable content.
  const config = { ADD_ATTR: ['target'] }
  if (allowedTags.length > 0) {
    config.ALLOWED_TAGS = allowedTags
  }
  // DOMPurify uses a flat array for ALLOWED_ATTR (not per-tag).
  // Merge all attribute arrays from the sanitize-html-style object.
  const attrs = Object.values(allowedAttributes).flat()
  if (attrs.length > 0) {
    config.ALLOWED_ATTR = [...new Set(attrs)]
  }
  return DOMPurify.sanitize(html, config)
}

export function linkifyAndSanitizeHtml(
  str = '',
  allowedTags = [],
  allowedAttributes = {},
) {
  const _allowedTags = ['a', ...allowedTags]
  const _allowedAttributes = {
    a: ['href', 'target', 'rel'],
    ...allowedAttributes,
  }
  const linkified = linkifyHtml(str, {
    validate: { url: (value) => /^https?:\/\//.test(value) },
    target: '_blank',
    rel: 'noopener noreferrer',
  })
  const sanitized = sanitizeHtml(linkified, _allowedTags, _allowedAttributes)
  return sanitized
}
